import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useNow } from '../../hooks/useMedia';
import { useT, type TKey } from '../../i18n';
import { formatDateTime, formatRelative } from '../../lib/device';
import { useSession } from '../../store/session';
import { useUi } from '../../store/ui';
import { ApiError, api, type AdminInvite, type AdminSettings, type AdminStats, type AdminUser } from '../../sync/api';
import controls from '../controls.module.css';
import { ChevronLeft } from '../icons';
import styles from './Admin.module.css';

type Tab = 'users' | 'invites' | 'settings';

export function Admin() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('users');
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Link to="/settings" className={controls.iconbtn} aria-label={t.t('nav.back')}>
          <ChevronLeft size={24} />
        </Link>
        <h1 className={`display ${styles.title}`}>{t.t('admin.title')}</h1>
        <div className={styles.tabs}>
          {(['users', 'invites', 'settings'] as Tab[]).map((k) => (
            <button key={k} type="button" className={`${styles.tab}${tab === k ? ` ${styles.on}` : ''}`} onClick={() => setTab(k)}>
              {t.t(`admin.${k}` as TKey)}
            </button>
          ))}
        </div>
      </header>
      <main className={styles.main}>
        {tab === 'users' ? <Users /> : tab === 'invites' ? <Invites /> : <Parameters />}
      </main>
    </div>
  );
}

function useToken(): string {
  return useSession((s) => s.token) ?? '';
}

function useFailure() {
  const t = useT();
  const showToast = useUi((s) => s.showToast);
  return (e: unknown) => showToast(t.t('admin.error', { message: e instanceof ApiError ? e.message : String(e) }));
}

function Users() {
  const t = useT();
  const now = useNow();
  const token = useToken();
  const me = useSession((s) => s.user);
  const fail = useFailure();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [temp, setTemp] = useState('');
  const [reveal, setReveal] = useState<string | null>(null);

  const load = async () => {
    try {
      setUsers((await api.admin.users(token)).users);
    } catch (e) {
      fail(e);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await api.admin.createUser(token, { username: username.trim().toLowerCase(), role, ...(temp ? { temporaryPassword: temp } : {}) });
      setReveal(t.t('admin.tempPasswordShown', { username: r.user.username, password: r.temporaryPassword }));
      setUsername('');
      setTemp('');
      await load();
    } catch (err) {
      fail(err);
    }
  };

  const patch = async (u: AdminUser, body: Parameters<typeof api.admin.patchUser>[2]) => {
    try {
      const r = await api.admin.patchUser(token, u.id, body);
      if (r.temporaryPassword) setReveal(t.t('admin.tempPasswordShown', { username: r.user.username, password: r.temporaryPassword }));
      await load();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <div className={styles.columns}>
      <section className={styles.panel}>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t.t('auth.username')}</th>
                <th>{t.t('admin.role')}</th>
                <th>{t.t('account.email')}</th>
                <th>{t.t('admin.created')}</th>
                <th>{t.t('admin.lastSignIn')}</th>
                <th>{t.t('admin.devices')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.disabledAt ? styles.off : undefined}>
                  <td>
                    <strong>{u.username}</strong>
                    {u.id === me?.id ? <span className={styles.pill}>{t.t('admin.you')}</span> : null}
                    {u.mustChangePassword ? <span className={`${styles.pill} ${styles.warn}`}>{t.t('admin.status.mustChange')}</span> : null}
                    {u.disabledAt ? <span className={styles.pill}>{t.t('admin.status.disabled')}</span> : null}
                  </td>
                  <td>{t.t(`admin.role.${u.role}` as TKey)}</td>
                  <td>
                    <input
                      type="email"
                      className={styles.emailField}
                      defaultValue={u.email ?? ''}
                      placeholder={t.t('account.emailPlaceholder')}
                      aria-label={`${t.t('account.email')} ${u.username}`}
                      onBlur={(e) => {
                        const next = e.target.value.trim() || null;
                        if (next !== (u.email ?? null)) void patch(u, { email: next });
                      }}
                    />
                  </td>
                  <td className="mono">{formatDateTime(u.createdAt, t.locale)}</td>
                  <td className="mono">{u.lastLoginAt ? formatRelative(u.lastLoginAt, now, t.locale) : t.t('admin.never')}</td>
                  <td className="mono">{u.devices}</td>
                  <td className={styles.actions}>
                    <button type="button" className={styles.action} onClick={() => void patch(u, { temporaryPassword: '' })}>
                      {t.t('admin.resetPassword')}
                    </button>
                    {u.id === me?.id ? null : (
                      <>
                        <button type="button" className={styles.action} onClick={() => void patch(u, { role: u.role === 'admin' ? 'user' : 'admin' })}>
                          {u.role === 'admin' ? t.t('admin.makeUser') : t.t('admin.makeAdmin')}
                        </button>
                        <button type="button" className={`${styles.action} ${u.disabledAt ? '' : styles.danger}`} onClick={() => void patch(u, { disabled: !u.disabledAt })}>
                          {u.disabledAt ? t.t('admin.enable') : t.t('admin.disable')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.note}>{t.t('admin.cardsNote')}</p>
      </section>
      <section className={styles.panel}>
        <h2 className={`display ${styles.panelTitle}`}>{t.t('admin.newUser')}</h2>
        <form className={styles.form} onSubmit={(e) => void create(e)}>
          <label className={styles.field}>
            <span>{t.t('auth.username')}</span>
            <input className={controls.field} value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" required />
          </label>
          <label className={styles.field}>
            <span>{t.t('admin.role')}</span>
            <select className={controls.field} value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
              <option value="user">{t.t('admin.role.user')}</option>
              <option value="admin">{t.t('admin.role.admin')}</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>{t.t('admin.tempPassword')}</span>
            <input className={controls.field} value={temp} onChange={(e) => setTemp(e.target.value)} autoComplete="off" />
            <span className={styles.note}>{t.t('admin.tempPasswordHint')}</span>
          </label>
          <button type="submit" className={`${controls.btn} ${controls.primary}`} style={{ alignSelf: 'flex-end' }}>
            {t.t('admin.create')}
          </button>
        </form>
        {reveal ? (
          <p className={styles.reveal} role="status">
            {reveal}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function Invites() {
  const t = useT();
  const now = useNow();
  const token = useToken();
  const fail = useFailure();
  const showToast = useUi((s) => s.showToast);
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [fresh, setFresh] = useState<string | null>(null);
  const load = async () => {
    try {
      setInvites((await api.admin.invites(token)).invites);
    } catch (e) {
      fail(e);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const create = async () => {
    try {
      const r = await api.admin.createInvite(token);
      setFresh(r.url);
      await copy(r.url);
      await load();
    } catch (e) {
      fail(e);
    }
  };
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast(t.t('admin.copied'));
    } catch {
      // clipboard blocked: the link stays on screen to select by hand
    }
  };
  const revoke = async (id: string) => {
    try {
      await api.admin.revokeInvite(token, id);
      await load();
    } catch (e) {
      fail(e);
    }
  };
  const state = (i: AdminInvite) =>
    i.usedAt ? t.t('admin.inviteUsed', { name: i.usedBy ?? '?' })
    : i.revokedAt ? t.t('admin.inviteRevoked')
    : i.expiresAt < now ? t.t('admin.inviteExpired')
    : t.t('admin.inviteExpires', { when: formatRelative(i.expiresAt, now, t.locale) });

  return (
    <section className={styles.panel}>
      <div className={styles.row}>
        <h2 className={`display ${styles.panelTitle}`}>{t.t('admin.invites')}</h2>
        <button type="button" className={`${controls.btn} ${controls.primary}`} style={{ flex: '0 0 auto' }} onClick={() => void create()}>
          {t.t('admin.newInvite')}
        </button>
      </div>
      {fresh ? (
        <p className={styles.reveal} role="status">
          {t.t('admin.inviteNew', { url: fresh })}{' '}
          <button type="button" className={styles.action} onClick={() => void copy(fresh)}>
            {t.t('admin.copy')}
          </button>
        </p>
      ) : null}
      {invites.length === 0 ? <p className={styles.note}>{t.t('admin.noInvites')}</p> : null}
      {invites.map((i) => (
        <div key={i.id} className={styles.row}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="mono">{formatDateTime(i.createdAt, t.locale)}</span>
            <span className={styles.note}>{state(i)}</span>
          </div>
          {!i.usedAt && !i.revokedAt && i.expiresAt >= now ? (
            <button type="button" className={`${styles.action} ${styles.danger}`} onClick={() => void revoke(i.id)}>
              {t.t('admin.revoke')}
            </button>
          ) : null}
        </div>
      ))}
    </section>
  );
}

function Parameters() {
  const t = useT();
  const token = useToken();
  const fail = useFailure();
  const showToast = useUi((s) => s.showToast);
  const refreshMeta = useSession((s) => s.refreshMeta);
  const [form, setForm] = useState<AdminSettings | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  useEffect(() => {
    void (async () => {
      try {
        setForm(await api.admin.settings(token));
        setStats(await api.admin.stats(token));
      } catch (e) {
        fail(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  if (!form) return <p className={styles.note}>{t.t('common.loading')}</p>;
  const update = <K extends keyof AdminSettings>(key: K, value: AdminSettings[K]) => setForm({ ...form, [key]: value });
  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setForm(await api.admin.saveSettings(token, form));
      await refreshMeta();
      showToast(t.t('admin.saved'));
    } catch (err) {
      fail(err);
    }
  };
  const labels = (key: 'defaultColumnLabels' | 'defaultBandLabels', i: number, v: string) => {
    const next = [...form[key]];
    next[i] = v.trim() ? v : null;
    update(key, next);
  };
  return (
    <div className={styles.columns}>
      <form className={`${styles.panel} ${styles.form}`} onSubmit={(e) => void save(e)}>
        <label className={styles.field}>
          <span>{t.t('admin.appTitle')}</span>
          <input className={controls.field} value={form.appTitle} onChange={(e) => update('appTitle', e.target.value)} maxLength={60} />
        </label>
        <div className={styles.field}>
          <span>{t.t('admin.registration')}</span>
          <div className={controls.segs} style={{ alignSelf: 'flex-start' }}>
            <button type="button" className={controls.seg} aria-pressed={!form.registrationOpen} onClick={() => update('registrationOpen', false)}>
              {t.t('admin.registration.closed')}
            </button>
            <button type="button" className={controls.seg} aria-pressed={form.registrationOpen} onClick={() => update('registrationOpen', true)}>
              {t.t('admin.registration.open')}
            </button>
          </div>
        </div>
        <div className={styles.twoUp}>
          <label className={styles.field}>
            <span>{t.t('admin.inviteExpiry')}</span>
            <span className={styles.number}>
              <input className={`${controls.field} mono`} type="number" min={1} max={365} value={form.inviteExpiryDays} onChange={(e) => update('inviteExpiryDays', Number(e.target.value))} />
              <span>{t.t('admin.days')}</span>
            </span>
          </label>
          <label className={styles.field}>
            <span>{t.t('admin.tombstone')}</span>
            <span className={styles.number}>
              <input className={`${controls.field} mono`} type="number" min={7} max={3650} value={form.tombstoneDays} onChange={(e) => update('tombstoneDays', Number(e.target.value))} />
              <span>{t.t('admin.days')}</span>
            </span>
            <span className={styles.note}>{t.t('admin.tombstoneHint')}</span>
          </label>
        </div>
        <div className={styles.field}>
          <span>{t.t('admin.defaultColumns')}</span>
          <div className={styles.labelGrid}>
            {form.defaultColumnLabels.map((v, i) => (
              <input key={i} className={controls.field} value={v ?? ''} placeholder={t.t(`column.${i}` as TKey)} onChange={(e) => labels('defaultColumnLabels', i, e.target.value)} />
            ))}
          </div>
        </div>
        <div className={styles.field}>
          <span>{t.t('admin.defaultBands')}</span>
          <div className={styles.labelGrid}>
            {form.defaultBandLabels.map((v, i) => (
              <input key={i} className={controls.field} value={v ?? ''} placeholder={t.t(`band.importance.${i}` as TKey)} onChange={(e) => labels('defaultBandLabels', i, e.target.value)} />
            ))}
          </div>
        </div>
        <div className={styles.twoUp}>
          <label className={styles.field}>
            <span>{t.t('admin.defaultLocale')}</span>
            <select className={controls.field} value={form.defaultLocale} onChange={(e) => update('defaultLocale', e.target.value as AdminSettings['defaultLocale'])}>
              <option value="auto">{t.t('settings.language.auto')}</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>{t.t('admin.defaultTimeFormat')}</span>
            <select className={controls.field} value={form.defaultTimeFormat} onChange={(e) => update('defaultTimeFormat', e.target.value as AdminSettings['defaultTimeFormat'])}>
              <option value="auto">{t.t('settings.timeFormat.auto')}</option>
              <option value="12h">{t.t('settings.timeFormat.12h')}</option>
              <option value="24h">{t.t('settings.timeFormat.24h')}</option>
            </select>
            <span className={styles.note}>{t.t('admin.defaultsHint')}</span>
          </label>
        </div>
        <label className={styles.row} style={{ justifyContent: 'flex-start', gap: 12 }}>
          <input type="checkbox" checked={form.apkDownloadEnabled} onChange={(e) => update('apkDownloadEnabled', e.target.checked)} style={{ width: 24, height: 24 }} />
          <span>{t.t('admin.apk')}</span>
        </label>
        <button type="submit" className={`${controls.btn} ${controls.primary}`} style={{ alignSelf: 'flex-end' }}>
          {t.t('admin.save')}
        </button>
      </form>
      <section className={styles.panel}>
        <h2 className={`display ${styles.panelTitle}`}>{t.t('admin.stats')}</h2>
        {stats ? (
          <dl className={styles.stats}>
            <dt>{t.t('admin.stats.users')}</dt>
            <dd className="mono">{stats.users}</dd>
            <dt>{t.t('admin.stats.cards')}</dt>
            <dd className="mono">{stats.openCards}</dd>
            <dt>{t.t('admin.stats.db')}</dt>
            <dd className="mono">{stats.dbBytes === null ? '?' : `${(stats.dbBytes / 1_048_576).toFixed(1)} MB`}</dd>
            <dt>{t.t('admin.stats.purge')}</dt>
            <dd className="mono">{stats.lastPurgeAt ? formatDateTime(stats.lastPurgeAt, t.locale) : t.t('admin.never')}</dd>
            <dt>{t.t('admin.stats.version')}</dt>
            <dd className="mono">
              {stats.version} · {stats.buildId}
            </dd>
          </dl>
        ) : null}
      </section>
    </div>
  );
}
